import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { FleetPage, FleetSection, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { Drawer } from '../../../../shared/drawer/drawer'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { IconCheck, IconList } from '../../../../shared/icon/icon'
import { Menu, MenuDelete, MenuItem, MenuRestore } from '../../../../shared/menu/menu'
import { MenuHeaderContext } from '../../../../shared/menu/menu-context'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { formatCount } from '../../../../shared/sso-workspace/format'
import { utcText } from '../../../../shared/date/local-time'
import { useRoleSysAiToolsTelemetry } from '../../../../role/hook/role-sys'
import { aiToolsAvailable, getSession, listSessionEvents } from './ai-tools-api'
import { EVENT_KIND_FILTERS, MarkPills, UNLINKED_DAY_NOTE, eventRow, isUnlinked, jsonText, marksCell, matchFilter, sessionRow, unlinkedDayText } from './ai-tools-words'
import { BinnedNotice, PersonValue, Row, UnlinkedNote, When, useRecordWrites } from './detail-parts'
import { FilterSelect, choiceSpec, useListFilters } from './ai-tools-filters'
import { MarkDialog } from './mark-dialog'
import { MarkableList, useMarkableList } from './markable-list'
import { useConsumers } from './use-consumers'
import { AiToolsTrail, tabCrumb } from './ai-tools-trail'
import '../../org/api-keys/api-keys.scss'

// One AI tools session (contract §4.6): what it is and who it is linked to
// (or Unlinked), its versions, times and counts, each consumer's mark, its
// session header as received, and every record of it, oldest first (ai's
// GET /admin/sessions/:id/events, by cursor, filtered by kind). A record
// opens in a drawer as received (the server drops "stamp" and "owner",
// contract R11); records and headers can be large, so their JSON only ever
// shows in a scrolling block, never in a grid cell. The page header's menu
// (the shared Menu, as record pages' headers have it) is Mark session
// (SYS_AI_TOOLS_TELEMETRY update) and Delete session, or Restore session
// for a session in the recycle bin (delete); a binned session says so above
// everything, with Restore, and its records can't be changed. The session's
// History is its record route's (record-routes.js: staff's moves, restores
// and marks of it are ai_tools.record_* events on it).
//
// The records are the same list the three tabs are (markable-list.js: paged
// by cursor, searched, reloaded in place by a filter, a mark or Try again,
// Mark and Delete from a row, select mode), in a flush grid under this
// page's own queryKey; the page adds the session's read, its own menu and
// the record drawer (page state, not the address). The open record is page
// state: it only exists while looked at.

const QUERY_KEY = 'session-records'
const FAILED = "This session's records couldn't be loaded."
const SOFT_LIMIT_NOTE = "Larger than its kind's soft limit (8 KB; 64 KB for a postmortem): stored whole, and counted in the daily summary."
const UNKNOWN_KIND_NOTE = "A kind the server doesn't know yet: stored whole, and counted in the daily summary."

// The records' kind filter, in the address like the lists' filters.
const RECORD_FILTERS = { kind: choiceSpec(EVENT_KIND_FILTERS) }
const recordLabel = row => `the ${row.kindText} record of ${row.whenText}`

// Grid cells (module-level, so AG Grid keeps its cells). The grid fits a
// 1280px window (owner 2026-10-29, "conservatively responsive"): the time
// with its day under it, a record's flags under its kind, its error under
// its tool, and the outcome with its duration in one column.
const whenCell = cell => {
  const row = cell.data
  if (!row || !row.ts) return ''
  return (
    <span className="ak-name-cell" title={`${row.whenText} · ${utcText(row.ts)}`}>
      <span>{row.whenTime}</span>
      <span className="sw-cell-sub">{row.whenDay}</span>
    </span>
  )
}
const kindCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (
    <span className="ak-name-cell">
      <span className="sw-mono">{row.kindText}</span>
      {(row.overSoftLimit || row.unknownKind) &&
        <span className="d-inline-flex flex-wrap gap-1">
          {row.overSoftLimit && <Pill tone="warn" title={SOFT_LIMIT_NOTE}>Over the soft limit</Pill>}
          {row.unknownKind && <Pill tone="info" title={UNKNOWN_KIND_NOTE}>Unknown kind</Pill>}
        </span>}
    </span>
  )
}
const toolCell = cell => {
  const row = cell.data
  if (!row) return ''
  return (
    <span className="ak-name-cell">
      <span className="sw-mono text-truncate" title={row.toolText}>{row.toolText}</span>
      {row.errorText && <span className="sw-cell-sub text-danger text-truncate" title={row.errorText}>{row.errorText}</span>}
    </span>
  )
}
const resultCell = cell => (cell.data ? cell.data.resultText : '')

const EventDrawer = ({ row, onClose }) => (
  <Drawer open size="lg" title={`${row.kindText} record · ${row.whenText}`} onClose={onClose}>
    <dl className="sw-dl">
      <Row label="When">{row.ts ? <time dateTime={row.ts} title={utcText(row.ts)}>{row.whenText}</time> : ''}</Row>
      <Row label="Kind"><span className="sw-mono">{row.kindText}</span></Row>
      <Row label="Tool">{row.toolText ? <span className="sw-mono">{row.toolText}</span> : ''}</Row>
      <Row label="Outcome">{row.outcomeText}</Row>
      <Row label="Time">{row.msText}</Row>
      <Row label="Response size">{typeof row.respBytes === 'number' ? `${formatCount(row.respBytes)} bytes` : ''}</Row>
      <Row label="Error">{row.errorText}</Row>
      <Row label="Record id">{row.recordId ? <span className="sw-mono">{row.recordId}</span> : ''}</Row>
      <Row label="Flags">{row.flagsText}</Row>
      <Row label="Marks"><MarkPills marks={row.marks} /></Row>
    </dl>
    <section aria-label="Record as received">
      <h3 className="h6">Record as received</h3>
      <pre className="ak-body sw-mono">{jsonText(row.record)}</pre>
    </section>
  </Drawer>
)

const linkedTo = s => (isUnlinked(s)
  ? <UnlinkedNote />
  : [s.linkText, s.userId ? `user #${s.userId}` : '', s.loginId ? `login #${s.loginId}` : ''].filter(Boolean).join(' · '))


const SessionPage = ({ sessionID }) => {
  const ids = { kind: useId() }
  const available = aiToolsAvailable()
  const role = useRoleSysAiToolsTelemetry()
  const [consumers, reloadConsumers] = useConsumers()
  const [session, setSession] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const { filters: { kind }, update } = useListFilters(RECORD_FILTERS)
  const [openRow, setOpenRow] = useState(null)
  const [markingSession, setMarkingSession] = useState(null)
  const reads = useRef(0)

  // undefined while the first read is out, null when it failed. A read after a
  // mark that fails keeps the session that is shown, with the notice above it;
  // an answer to an earlier read never replaces a later one's.
  const load = useCallback(() => {
    if (!aiToolsAvailable()) return Promise.resolve()
    const mine = ++reads.current
    return getSession(sessionID).then(
      body => {
        if (mine !== reads.current) return
        if (body && body.session) { setError(null); setSession(sessionRow(body.session)) } else { setSession(current => current || null); setError("This session couldn't be loaded.") }
      },
      err => {
        if (mine !== reads.current) return
        setSession(current => current || null)
        setError(serverMessage(err, "This session couldn't be loaded."), err)
      }
    )
  }, [sessionID, setError])
  useEffect(() => { load() }, [load])

  const binned = !!(session && session.deletedAt)
  const canMark = !!role.accessUpdate && !binned
  const fetchEvents = useCallback(params => listSessionEvents(sessionID, params), [sessionID])
  const filters = useMemo(() => ({ kind }), [kind])
  // the records' selection: this session's, of the kind shown, as searched
  const matchOf = useCallback((f, q) => matchFilter('event', { ...f, session_id: sessionID, q }), [sessionID])
  const list = useMarkableList({
    fetchPage: fetchEvents, toRow: eventRow, filters, failedText: FAILED, kind: 'event', labelOf: recordLabel,
    consumers, onMarked: reloadConsumers, onNeedConsumers: reloadConsumers, matchOf, writable: !binned, recycleTab: 'records'
  })
  const writes = useRecordWrites({ kind: 'session', onChanged: load })

  const askMarkSession = () => {
    if (!(consumers && consumers.length)) reloadConsumers()
    setMarkingSession({ kind: 'session', id: session.id, label: `session ${session.sessionText}` })
  }
  // A save that finishes after its dialog was cancelled still counts (the page
  // reads again) but closes only its own dialog, never one opened since.
  const sessionMarked = target => {
    setMarkingSession(current => (current === target ? null : current))
    load()
    reloadConsumers()
  }

  const s = session
  const canDelete = !!role.accessDelete
  const headerMenu = available && s && (canMark || canDelete)
    ? <MenuHeaderContext.Provider value={true}>
        <Menu>
          <MenuItem Icon={IconCheck} text="Mark session" pin show={canMark} onClick={askMarkSession} />
          <MenuDelete text="Delete session" show={canDelete && !binned} onClick={() => writes.askDelete(s.id)} />
          <MenuRestore text="Restore session" show={canDelete && binned} disabled={writes.busy} onClick={() => writes.restore(s.id)} />
        </Menu>
      </MenuHeaderContext.Provider>
    : null
  return (
    <FleetPage
      title={s ? `Session ${s.sessionText}` : 'Session'}
      purpose={s ? (s.channelText ? `${s.sourceText} · ${s.channelText}` : s.sourceText) : null}
      actions={headerMenu}
      testId="ai-tools-session-page">
      <AiToolsTrail crumbs={[tabCrumb('sessions'), { url: `/admin/ai-tools/sessions/${sessionID}`, text: s ? s.sessionText : '' }]} />
      <p className="mb-2"><Link to="/admin/ai-tools?tab=sessions">← All sessions</Link></p>
      {!available && <Notice tone="warn">AI tools telemetry isn't available in this build.</Notice>}
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {available && s && binned && <BinnedNotice row={s} kind="session" />}
      {writes.notice}
      {available && s === undefined && !error && <p className="sw-hint" role="status">Loading…</p>}
      {available && s &&
        <>
          <FleetSection id="ait-session" title="About this session">
            <dl className="sw-dl">
              <Row label="Session"><span className="sw-mono">{s.sessionText}</span></Row>
              <Row label="Source">{s.sourceText}</Row>
              <Row label="Channel">{s.channelText}</Row>
              <Row label="Person">{s.person ? <PersonValue person={s.person} /> : null}</Row>
              <Row label="Linked to">{linkedTo(s)}</Row>
              <Row label="Install"><span className="sw-mono">{s.install}</span></Row>
              <Row label="Project">{s.projectHash ? <span className="sw-mono">{s.projectHash}</span> : ''}</Row>
              <Row label="Versions">{s.versionsText}</Row>
              <Row label="Platform">{s.platform}</Row>
              <Row label="First record"><When value={s.firstSeenTs} /></Row>
              <Row label="Last record"><When value={s.lastSeenTs} /></Row>
              <Row label="Received">{isUnlinked(s) && s.receivedAt ? <span title={UNLINKED_DAY_NOTE}>{unlinkedDayText(s.receivedAt)}</span> : <When value={s.receivedAt} />}</Row>
              <Row label="Records">{s.countsText}</Row>
              <Row label="Marks"><MarkPills marks={s.marks} /></Row>
            </dl>
          </FleetSection>

          <FleetSection id="ait-header" title="Session header" lede="The session's header record as received: the tools' versions and settings when it began.">
            {s.header
              ? <pre className="ak-body sw-mono">{jsonText(s.header)}</pre>
              : <p className="sw-hint m-0">No session header has arrived for this session yet.</p>}
          </FleetSection>

          <FleetSection id="ait-records" title="Records" lede="Every record of this session in the order it happened, oldest first. Open one to read it as received.">
            <MarkableList list={list} queryKey={QUERY_KEY} title="Records" subtitle="Oldest first" flush onRowOpen={setOpenRow}
              controls={<FilterSelect id={ids.kind} label="Kind" value={kind} onChange={list.change(value => update({ kind: value }))} options={EVENT_KIND_FILTERS} />}
              emptyState={{ Icon: IconList, title: 'No records', message: 'No record of this session matches this kind.' }}>
              <ColumnFormat field="whenText" header="When" sortable={false} fit="content" stacked render={whenCell} />
              <ColumnFormat field="kindText" header="Kind" sortable={false} fit="content" stacked render={kindCell} />
              <ColumnFormat field="toolText" header="Tool" sortable={false} flex={1.2} textMax={240} stacked render={toolCell} />
              <ColumnFormat field="resultText" header="Outcome" sortable={false} fit="content" render={resultCell} />
              <ColumnFormat field="marksText" header="Marks" sortable={false} flex={1} render={marksCell} />
            </MarkableList>
          </FleetSection>
        </>}
      {openRow && <EventDrawer row={openRow} onClose={() => setOpenRow(null)} />}
      {markingSession && <MarkDialog target={markingSession} consumers={consumers} onCancel={() => setMarkingSession(null)} onMarked={() => sessionMarked(markingSession)} />}
      {writes.dialog}
    </FleetPage>
  )
}

// A session's page: another session (the address changed under the mounted
// page) is a fresh page, so nothing of the last one's records, filter or open
// record carries over.
export const SessionDetail = () => {
  const { sessionID } = useParams()
  return <SessionPage key={sessionID} sessionID={sessionID} />
}
